import { RadioGroup } from '@arcadiapower/shrike';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '~/components/button';
import { type ImageType, TERMS_IMAGE, ImageSelector } from '~/components/image';
import { Inputs } from '~/components/inputs';
import { Container, TwoColumnGrid } from '~/components/layout';
import { ReviewBlock } from '~/components/review-block';
import { Description, Title, Text } from '~/components/typography';
import { useCopy } from '~/hooks';
import { LmiStatus } from '~/machines/review-form';
import { SelfAttestationData } from '~/screens/vnm-terms';
import { SelfAttestationForm } from '~/screens/vnm-terms/self-attestation-form/self-attestation-form';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchUtilityAccountResponse } from '~/services/fetch-utility-account';
import { AddressType, BillingDataSource, ContractVersion, Utility } from '~/typings/context';

export const ReviewForm = ({ context, next, overrideCopy, loading, flags }: ReviewFormProps) => {
  const copy = useCopy(context, checkCopy, overrideCopy);
  const [selfAttestationFormData, setSelfAttestationFormData] =
    useState<SelfAttestationData | null>(null);
  const lmiOptions: LmiStatus[] = ['isLmi', 'maybeLmi', 'notLmi'];
  const [lmiStatus, setLmiStatus] = useState<LmiStatus>('isLmi');
  const [nextButtonText, setNextButtonText] = useState(copy.next);
  const [address, setAddress] = useState<AddressType>();
  const [isAddressPresent, setIsAddressPresent] = useState(false);
  const state = context.funnelPacket?.utility?.state;

  // TO DO : Ideally this should be handled inside the wrapper component
  // This is a temporary fix as the overrideCopy is not currently passed to wrapper component

  const isSponsoredSolar =
    context.funnelPacket?.user?.originationSource?.originationSourceType === 'sponsored_solar';
  const defaultDescriptionLineOne = isSponsoredSolar
    ? copy.descriptionLineOneSponsoredSolar
    : copy.defaultDescriptionLineOne;
  const defaultDescriptionLineTwo = isSponsoredSolar
    ? copy.defaultDescriptionLineTwo
    : copy.descriptionLineTwo;

  /**
   * If any of the dropdown in self attestation is not selected, the next button should be disabled.
   * Filtered out the lmiProgram field as it is optional.
   */
  const shouldBeDisabled = selfAttestationFormData
    ? Object.keys(selfAttestationFormData)
        .filter(key => key !== 'lmiProgram')
        .some(value => selfAttestationFormData[value] === '')
    : false;

  const getSelfAttestationSection = () => {
    /**
     * if lmi option radio buttons are shown,
     * then only show self attestation form when the user chose "yes! I am LMI"
     * or "I don't know"(maybeLmi).
     *
     * if the radio buttons are not shown, then always show self attestation
     * form.
     */
    if (!flags?.showSelfAttestationForm) {
      return null;
    }

    if (!flags.showLmiOptions) {
      return <SelfAttestationForm context={context} onFormUpdate={setSelfAttestationFormData} />;
    }

    const hasUserChoseLmi = lmiStatus === 'isLmi' || lmiStatus === 'maybeLmi';

    return hasUserChoseLmi ? (
      <SelfAttestationForm context={context} onFormUpdate={setSelfAttestationFormData} />
    ) : null;
  };

  useEffect(() => {
    switch (lmiStatus) {
      case 'isLmi':
        setNextButtonText(copy.next);
        break;
      case 'maybeLmi':
        setNextButtonText(copy.next);
        break;
      case 'notLmi':
        setNextButtonText('Continue');
        break;
      default:
        setNextButtonText(copy.next);
    }
  }, [lmiStatus]);

  const isLmiStatusValid = lmiStatus !== 'notLmi';
  useEffect(() => {
    if (!address) {
      setIsAddressPresent(false);
    } else {
      setIsAddressPresent(Object.values(address).every(value => value.trim() !== ''));
    }
  }, [address]);

  const handleAddressChange = useCallback(
    (address: AddressType | undefined) => {
      if (!address) setAddress(undefined);
      if (address?.street) {
        setAddress(address);
      }
    },
    [address?.street],
  );

  const shouldDisableButton =
    state?.abbreviation === 'NJ' && flags?.requiresAddressFields && isLmiStatusValid
      ? !isAddressPresent || shouldBeDisabled
      : shouldBeDisabled && isLmiStatusValid;

  return (
    <TwoColumnGrid testId="screen-Review-Form">
      <TwoColumnGrid.Primary>
        <Title fontWeight={700} fontSize="24px">
          {copy.title}
        </Title>
        {defaultDescriptionLineOne && <Description mb={4}>{defaultDescriptionLineOne}</Description>}
        {defaultDescriptionLineTwo && <Description mb={4}>{defaultDescriptionLineTwo}</Description>}
        {flags?.showLmiOptions && (
          <ReviewBlock>
            <ReviewBlock.Item label={copy.lmiSectionTitle} notice={copy.lmiSectionDescription} />
            <RadioGroup
              margin="24px 0 0 0"
              name="lmi-selection"
              options={(lmiOptions as LmiStatus[]).map(choice => ({
                id: choice,
                children: (
                  <Container key={choice} onClick={() => setLmiStatus(choice)}>
                    {copy[choice].primary}
                    <Text fontSize="small" margin="10px 0">
                      {copy[choice].secondary}
                    </Text>
                  </Container>
                ),
                value: choice,
              }))}
              isChecked={(_, value) => value === lmiStatus}
              onChange={(selection: LmiStatus) => setLmiStatus(selection)}
              width="initial"
            />
          </ReviewBlock>
        )}
        {isLmiStatusValid && flags?.requiresAddressFields ? (
          <Inputs.Address mb={5} state={state} onChange={handleAddressChange} />
        ) : null}
        {getSelfAttestationSection()}
        <Button
          disabled={shouldDisableButton}
          mb={4}
          mode="primary"
          testId="review-form-button"
          onClick={() => {
            next({
              SelfAttestationData: selfAttestationFormData,
              lmiStatus,
              address,
            });
          }}
          loading={loading}
        >
          {nextButtonText}
        </Button>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const checkCopy = () => {
  return defaultCopy();
};

export const defaultCopy = () => {
  return {
    title: 'Review your subscriber income verification form',
    defaultDescriptionLineOne:
      'To find out if you are eligible for additional savings, your state needs you to fill out an income status verification form. ',
    defaultDescriptionLineTwo:
      'Click “Review form” below and you will be taken to a new page that provides more details where you can self-attest to your household income level.',
    imageType: TERMS_IMAGE as ImageType,
    next: 'Review form',
    nonLmiNext: 'Continue',
    descriptionLineOneSponsoredSolar:
      'To find out if you are eligible for additional savings, please fill out an income status verification form. ',
    descriptionLineOneNJSelfAttestation:
      'To find out if you are eligible for additional savings, your state needs you to fill out an income status verification form.',
    descriptionLineTwoNJSelfAttestation:
      "First, select your county, household occupancy, and low-to-moderate income qualifying program (if applicable). Then click 'Review form' and you will be taken to a new page that requires your confirmation.",
    descriptionLineOneMDSelfAttestation:
      'Households qualify as low and moderate income based on criteria specified by program rules',
    descriptionLineTwo: '',
    descriptionLineOne: '',
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    isLmi: {
      primary: 'Yes, I qualify as LMI',
      secondary:
        'If you choose this option, after clicking "Review form" below, you will see a form to self-attest your income status.',
    },
    maybeLmi: {
      primary: "I don't know if I qualify as LMI",
      secondary:
        'If you aren\'t sure, that\'s ok. After clicking "Review form" below, you will see a form to attest your income status. Go ahead and sign it. We will review your income level and check whether you qualify as LMI.',
    },
    notLmi: {
      primary: 'No, I do not qualify as LMI',
      secondary: "You're still guaranteed to save with community solar!",
    },
  };
};

export type ReviewFormCopy = ReturnType<typeof defaultCopy>;

export interface ReviewFormProps {
  context: ReviewFormContext;
  loading: boolean;
  next: (data) => void;
  flags?: {
    showSelfAttestationForm: boolean;
    showLmiOptions: boolean;
    requiresAddressFields: boolean;
  };
  overrideCopy?: (context: ReviewFormContext) => Partial<ReturnType<typeof defaultCopy>>;
}

export interface ReviewFormContext {
  contracts: ContractVersion[];
  billingSelection?: BillingDataSource;
  businessName?: string;
  email: string;
  funnelPacket: FetchFunnelPacketResponse;
  phoneNumber?: string | null;
  firstName: string;
  lastName: string;
  lmiStatus?: string;
  paymentMethod?: { lastFour: string; source: string };
  promoCode?: string; // used in solar landscape copy
  solarSavingsRate?: string;
  utility: Utility;
  isUtilityScraperEligible?: boolean;
  utilityAccount?: FetchUtilityAccountResponse['utilityAccount'];
}

export interface FetchUtilityScraperEligibleOptions {}
